import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Loading, SidePanel } from 'design-system-web';
import { Alert } from 'components/lds';
import { TranslationKeys } from 'localization';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { Channel } from 'application-types';
import {
  getBrowserTimeZone,
  getChannelTimezone,
} from 'domains/workOrder/serviceRequest/helpers/getChannelTimezone';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import {
  ALL_ISSUE_TYPES_VALUE,
  buildIssueTypeItems,
  findSelectedCategory,
  resolveIssues,
  shouldCreateNoneWithAllIssueTypes,
} from './coverageHoursPanelUtils';
import { toProtoDayNames } from './coverageHoursRowFormat';
import { minutesOfDay } from './coverageHoursTimeUtils';
import { useCoverageHoursPanelOptions } from './useCoverageHoursPanelOptions';
import {
  CoverageHoursEntry,
  CoverageHoursPageInfo,
  CoverageHoursQueryError,
  useAllCoverageHours,
} from './useCoverageHours';
import { useCreateCoverageHours } from './useCreateCoverageHours';
import { CoverageHoursForm } from './CoverageHoursForm';
import { CoverageHoursPanelFooter } from './CoverageHoursPanelFooter';

const TRANSLATION_KEYS: Record<'title' | 'saveError' | 'loadError', TranslationKeys> =
  Object.freeze({
    title: 'web.admin.serviceRequest.settings.coverageHours.create.title',
    saveError: 'web.admin.serviceRequest.settings.coverageHours.create.saveError',
    loadError: 'web.admin.serviceRequest.settings.coverageHours.error.load',
  });

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  channel?: Channel;
  categories: WorkOrderModuleCategoriesSettings[];
  onOptimisticAdd: (rows: CoverageHoursEntry[]) => void;
  onCreateSuccess: () => void;
  onCreateError: (localIds: string[]) => void;
};

// pageInfo, unlike loading, stays null until the first response ever
// arrives - so this only gates the initial load, not later refetches.
// existingEntries can't be trusted at all once the fetch has errored, so
// the form stays hidden rather than rendering against a false "nothing
// exists yet" default.
function CoverageHoursPanelLoadingShell({
  loading,
  pageInfo,
  error,
  children,
}: {
  loading: boolean;
  pageInfo: CoverageHoursPageInfo | null;
  error?: CoverageHoursQueryError;
  children: React.ReactElement;
}) {
  if (error) {
    return null;
  }

  if (loading && !pageInfo) {
    return <Loading />;
  }

  return children;
}

export function CoverageHoursPanel({
  isOpen,
  onClose,
  channelId,
  channel,
  categories,
  onOptimisticAdd,
  onCreateSuccess,
  onCreateError,
}: Props) {
  const { t } = useTranslation();
  const [, , assignableTeams] = useAssignableMembers(channelId);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [issueTypeValue, setIssueTypeValue] = useState<string>(
    ALL_ISSUE_TYPES_VALUE
  );
  const [savedTeams, setSavedTeams] = useState<TeamCoverageEntry[]>([]);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [locallyCreatedEntries, setLocallyCreatedEntries] = useState<
    CoverageHoursEntry[]
  >([]);
  const {
    entries: existingEntries,
    pageInfo,
    loading: entriesLoading,
    error: existingEntriesError,
    fetchCoverageHours: fetchExistingEntries,
  } = useAllCoverageHours(channelId);

  const allExistingEntries = useMemo(
    () => [...existingEntries, ...locallyCreatedEntries],
    [existingEntries, locallyCreatedEntries]
  );

  const { selectedCategory } = useCoverageHoursPanelOptions(
    categories,
    categoryValue,
    allExistingEntries
  );
  const { createCoverageHours } = useCreateCoverageHours();

  const hasUnsavedChanges = savedTeams.length > 0;
  const canSave =
    Boolean(categoryValue) &&
    savedTeams.length > 0 &&
    !isSaving &&
    !existingEntriesError;

  useEffect(() => {
    if (!isOpen) {
      setIsConfirmingClose(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchExistingEntries();
      setLocallyCreatedEntries([]);
    }
  }, [isOpen, fetchExistingEntries]);

  const handleCategoryChange = (value: string) => {
    setCategoryValue(value);

    const newSelectedCategory = findSelectedCategory(categories, value);
    const newIssueTypeItems = buildIssueTypeItems(
      newSelectedCategory,
      allExistingEntries,
      t
    );

    setIssueTypeValue(newIssueTypeItems[0]?.value ?? ALL_ISSUE_TYPES_VALUE);
  };

  const handleClose = () => {
    setCategoryValue(null);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
    setSavedTeams([]);
    setIsConfirmingClose(false);
    setSaveError(false);
    onClose();
  };

  const handleSaveTeam = (entry: TeamCoverageEntry) => {
    setSavedTeams(prev => {
      const idx = prev.findIndex(team => team.id === entry.id);

      if (idx >= 0) {
        const next = [...prev];

        next[idx] = entry;

        return next;
      }

      return [...prev, entry];
    });
  };

  const handleOnCancel = () => {
    if (hasUnsavedChanges) {
      setIsConfirmingClose(true);
    } else {
      handleClose();
    }
  };

  const handleSave = async () => {
    if (!categoryValue) {
      return;
    }

    setSaveError(false);
    setIsSaving(true);

    let optimisticRows: CoverageHoursEntry[] = [];

    try {
      const issues = resolveIssues(
        issueTypeValue,
        selectedCategory,
        allExistingEntries
      );
      const timezone =
        (channel?.address ? getChannelTimezone(channel.address) : null) ??
        getBrowserTimeZone();

      const teamsWithBlocks = savedTeams.map(team => ({
        team,
        blocks: team.coverageBlocks.map(block => ({
          daysOfWeek: toProtoDayNames(block.daysOfWeek),
          startMinute: minutesOfDay(block.startTime),
          endMinute: minutesOfDay(block.endTime),
        })),
      }));

      const teams = teamsWithBlocks.map(({ team, blocks }) => ({
        teamId: team.teamId,
        blocks,
      }));

      const shouldAlsoCreateNone = shouldCreateNoneWithAllIssueTypes(
        issueTypeValue,
        selectedCategory,
        allExistingEntries
      );

      // Fresh array - must not mutate `issues`, still needed as-is below.
      const issuesForRows: (string | null)[] =
        issues.length > 0 ? [...issues] : [null];

      if (shouldAlsoCreateNone) {
        issuesForRows.push(null);
      }

      optimisticRows = teamsWithBlocks.flatMap(({ team, blocks }) =>
        blocks.flatMap(block =>
          issuesForRows.map(issue => ({
            id: uuidv4(),
            routingId: uuidv4(),
            category: categoryValue,
            issue,
            teamId: team.teamId,
            teamName: team.teamLabel,
            daysOfWeek: block.daysOfWeek,
            startMinute: block.startMinute,
            endMinute: block.endMinute,
            timezone,
          }))
        )
      );

      onOptimisticAdd(optimisticRows);

      await createCoverageHours({
        channelId,
        category: categoryValue,
        issues,
        timezone,
        teams,
      });

      if (shouldAlsoCreateNone) {
        // issues can only mean "one per listed issue" or "a single
        // category-level routing" when empty - never both in one request.
        await createCoverageHours({
          channelId,
          category: categoryValue,
          issues: [],
          timezone,
          teams,
        });
      }

      onCreateSuccess();
      setLocallyCreatedEntries(prev => [...prev, ...optimisticRows]);
      handleClose();
    } catch {
      setSaveError(true);

      if (optimisticRows.length > 0) {
        onCreateError(optimisticRows.map(row => row.id));

        try {
          // Covers a partial success (issues created, then the None call
          // failed) - refetch so a retry sees what's already there.
          const freshEntries = await fetchExistingEntries();

          setLocallyCreatedEntries([]);

          const freshIssueTypeItems = buildIssueTypeItems(
            selectedCategory,
            freshEntries,
            t
          );

          if (
            !freshIssueTypeItems.some(item => item.value === issueTypeValue)
          ) {
            setIssueTypeValue(
              freshIssueTypeItems[0]?.value ?? ALL_ISSUE_TYPES_VALUE
            );
          }
        } catch {
          // The save's own error is already shown above; nothing further
          // to reconcile without fresh data from this second failure.
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SidePanel
      id="create-coverage-hours-panel"
      isOpen={isOpen}
      onClose={isSaving ? undefined : handleClose}
      header={t(TRANSLATION_KEYS.title)}
      size="medium"
      backdropEnabled
      footerActions={
        <CoverageHoursPanelFooter
          isConfirmingClose={isConfirmingClose}
          onDiscardChanges={handleClose}
          onKeepEditing={() => setIsConfirmingClose(false)}
          onCancel={handleOnCancel}
          onSave={handleSave}
          isSaving={isSaving}
          canSave={canSave}
        />
      }
    >
      {saveError && (
        <Alert type="error">{t(TRANSLATION_KEYS.saveError)}</Alert>
      )}
      {existingEntriesError && (
        <Alert type="error">{t(TRANSLATION_KEYS.loadError)}</Alert>
      )}
      <CoverageHoursPanelLoadingShell
        loading={entriesLoading}
        pageInfo={pageInfo}
        error={existingEntriesError}
      >
        <CoverageHoursForm
          categories={categories}
          existingEntries={allExistingEntries}
          categoryValue={categoryValue}
          onCategoryChange={handleCategoryChange}
          issueTypeValue={issueTypeValue}
          onIssueTypeChange={setIssueTypeValue}
          assignableTeams={assignableTeams}
          savedTeams={savedTeams}
          onSaveTeam={handleSaveTeam}
          channelId={channelId}
        />
      </CoverageHoursPanelLoadingShell>
    </SidePanel>
  );
}
